import React from "react";
import Modal from "../../commonComponents/util/Modal";
import RadioGroup from "../../commonComponents/util/RadioGroup";
import Select from "../../commonComponents/util/Select";
import FadeOutErrorMessage from "../../commonComponents/util/FadeOutErrorMessage";

import {
  convertToCSV,
  downloadCSV,
  formatCurrency,
  formatDate
} from "../../../lib/utils";
import { TransactionTypes } from "../../../lib/constants";
import { BigNumber } from "bignumber.js";

const ReportTypes = {
  CashReport: "Cash Report",
  CheckRegisterReport: "Check Register Report",
  ClearedCheckReport: "Cleared Check Report",
  OutstandingTransactionsReport: "Outstanding Transactions Report",
  OutsideTransactionsReport: "Outside Transactions Report",
  BankReconciliationReport: "Bank Reconciliation Report"
};

const DefaultFields = {
  CheckRegisterReport: [
    "TransDate",
    "RefNumber",
    "LastOrOrgName",
    "FirstName",
    "ReportName",
    "ReportMemo",
    "TransAmount",
    "Status"],
  OutstandingTransactionsReport: ["TransAmount"]
};

function getReportFields(reportType) {
  return [
    { label: "Date", value: "TransDate", default: "" },
    { label: "Reference Number", value: "RefNumber", default: "" },
    { label: "Last Name/Org Name", value: "LastOrOrgName", default: "" },
    { label: "First Name", value: "FirstName", default: "" },
    { label: "Title", value: "Title", default: "" },
    { label: "Report Name", value: "ReportName", default: "" },
    { label: "Bank Info", value: "BankAccountInfo", default: "" },
    { label: "Bank Account Holder", value: "BankAccountHolder", default: "" },
    { label: "Paid To", value: "PaidTo", default: "" },
    { label: "Type", value: "Type", default: "" },
    { label: "Deposit Source / Withdrawal Type", value: "Source", default: "" },
    { label: "Description", value: "Description", default: "" },
    { label: "Check Memo", value: "CheckMemo", default: "" },
    { label: "Report Memo", value: "ReportMemo", default: "" },
    { label: "Address", value: "Address", default: "" },
    { label: "City", value: "City", default: "" },
    { label: "State", value: "State", default: "" },
    { label: "Zip", value: "Zip", default: "" },
    { label: "Country", value: "Country", default: "" },
    { label: "Teudat Zehut", value: "TeudatZehut", default: "" },
    { label: "Tax Id", value: "TaxId", default: "" },
    { label: "Approver", value: "Approver", default: "" },
    ...(reportType === ReportTypes.CashReport
      ? [
        {
          label: "Debit",
          value: "Debit",
          getValue: ({ Debit }) => formatCurrency(Debit, null, true),
          default: ""
        },
        {
          label: "Credit",
          value: "Credit",
          getValue: ({ Credit }) => formatCurrency(Credit, null, true),
          default: ""
        },
        {
          label: "Book Balance",
          value: "Balance",
          getValue: ({ Balance }) => formatCurrency(Balance, null, true),
          default: ""
        }
      ]
      : [
        {
          label: "Amount",
          value: "TransAmount",
          getValue: ({ TransAmount }) => reportType === ReportTypes.CheckRegisterReport ? formatCurrency(TransAmount, null, true, true) : formatCurrency(TransAmount, null, true),
          default: "",
          disabled: reportType === ReportTypes.OutstandingTransactionsReport
        }
      ]),
    { label: "Currency", value: "Currency", default: "" },
    { label: "Status", value: "Status", default: "" },
    ...(reportType !== ReportTypes.OutsideTransactionsReport
      && [
        { label: "Reference(s)", value: "References", default: "" },
        { label: "Solicitor(s)", value: "Solicitors", default: "" },
        { label: "Rosh Yeshiva(s)", value: "RoshYeshivas", default: "" }
      ]),
  ];
}

const initialState = {
  fields: [],
  groupBy: "",
  type: ReportTypes.CashReport,
  error: false,
  noData: false,
  downloading: false,
  submitAttempted: false,
  excludeVoidedChecks: false
};

export default class ReportModal extends React.Component {
  constructor(props) {
    super(props);

    this.state = initialState;
  }

  componentWillReceiveProps(nextProps) {
    if (nextProps.show && !this.props.show && nextProps.reportType) {
      const type = ReportTypes[nextProps.reportType];
      this.setState({ type });
      //default selected fields for reconciliation reports
      if (type === ReportTypes.BankReconciliationReport) {
        this.setState({
          fields: [
            "TransDate",
            "RefNumber",
            "PaidTo",
            "Type",
            "Source",
            "TransAmount",
            "Status"
          ]
        });
      }
    }
  }

  onChange = (name, value) => {
    this.setState({ [name]: value });
  };

  onToggleReportType = reportType => {
    var reportTypeKey = Object.keys(ReportTypes).find(key => ReportTypes[key] === reportType);
    this.setState({
      error: false,
      fields: DefaultFields[reportTypeKey] ? DefaultFields[reportTypeKey] : [],
      groupBy: "",
      submitAttempted: false,
      type: reportType
    });
  };

  onToggleReportFields = event => {
    const fieldName = event.target.name;
    const checked = event.target.checked;

    let fields = [...this.state.fields];
    if (checked) {
      fields.push(fieldName);
    } else {
      fields = fields.filter(f => f !== fieldName);

      if (this.state.groupBy === fieldName) {
        this.setState({ groupBy: "" });
      }
    }

    if (this.state.type === ReportTypes.CashReport) {
      //if balance is checked: cannot group results & credit, debit, and date must be checked
      if (fieldName === "Balance" && checked) {
        if (this.state.groupBy) {
          this.setState({ groupBy: "" });
        }
        if (fields.indexOf("Credit") < 0) {
          fields.push("Credit");
        }
        if (fields.indexOf("Debit") < 0) {
          fields.push("Debit");
        }
        if (fields.indexOf("TransDate") < 0) {
          fields.push("TransDate");
        }
      } else if (
        (fieldName === "Credit" ||
          fieldName === "Debit" ||
          fieldName === "TransDate") &&
        !checked
      ) {
        if (fields.indexOf("Balance") >= 0) {
          fields = fields.filter(f => f !== "Balance");
        }
      }
    }

    this.setState({ fields });
  };

  close() {
    this.setState(initialState);
    this.props.close();
  }

  excludeVoidedChecks = event => {
    this.setState({excludeVoidedChecks: event.target.checked })
  }

  async exportData() {
    this.setState({
      error: false,
      noData: false,
      submitAttempted: true
    });

    const { fields, groupBy, type } = this.state;
    const { tableSettings, filterSettings } = this.props;

    if (!fields.length) {
      return;
    }

    this.setState({
      downloading: true
    });

    try {
      let exportHeaders;
      let reportName;
      const dateNow = `${new Date().toLocaleDateString().replace(/\//g, "_")}`;

      const reportFields = getReportFields(type);

      let params = {
        fields,
        groupBy,
        includeOpeningBalance: true,
        ...(type != ReportTypes.BankReconciliationReport && type != ReportTypes.OutstandingTransactionsReport &&
          {
            sortBy: tableSettings.sort.by,
            sortDirection: tableSettings.sort.direction,
            startDate: filterSettings.startDate,
            endDate: filterSettings.endDate
          }
        )
      };
      switch (type) {
        case ReportTypes.CashReport:
          params = {
            ...params,
            statuses: ["cleared", "outstanding"]
          };
          exportHeaders = reportFields.filter(
            field => fields.indexOf(field.value) >= 0
          );
          reportName = `cash_${dateNow}`;
          break;
        case ReportTypes.CheckRegisterReport:
          params = {
            ...params,
            sources: ["systemCheck", "manualCheck", "systemWire", "manualWire", "systemACH"],
            statuses: this.state.excludeVoidedChecks ? ["Outstanding", "Cleared"] : []
          };
          exportHeaders = reportFields.filter(
            field => fields.indexOf(field.value) >= 0
          );
          reportName = `check_register_${dateNow}`;
          break;
        case ReportTypes.ClearedCheckReport:
          params = {
            ...params,
            useClearedDate: true,
            sources: ["systemCheck", "manualCheck"],
            statuses: "cleared"
          };
          exportHeaders = reportFields.filter(
            field => fields.indexOf(field.value) >= 0
          );
          reportName = `cleared_check_${dateNow}`;
          break;
        case ReportTypes.OutstandingTransactionsReport:
          params = {
            ...params,
            statuses: "outstanding"
          };
          exportHeaders = reportFields.filter(
            field => fields.indexOf(field.value) >= 0
          );
          reportName = `outstanding_transactions_${dateNow}`;
          break;
        case ReportTypes.OutsideTransactionsReport:
          params = {
            ...params,
            sources: ["manual"]
          };
          exportHeaders = reportFields.filter(
            field => fields.indexOf(field.value) >= 0
          );
          reportName = `outside_transactions_${dateNow}`;
          break;
        case ReportTypes.BankReconciliationReport:
          params = {
            ...params,
            report: true
          };
          exportHeaders = reportFields.filter(
            field => fields.indexOf(field.value) >= 0
          );
          reportName =
            this.props.reportName || `bank_reconciliation_${dateNow}`;
          break;
        default:
          params = {};
          exportHeaders = [];
          reportName = `report_${dateNow}`;
      }

      const response = await this.props.getTransactionsForExport(params);

      if (!response.success) throw Error("error");

      const records =
        type === ReportTypes.BankReconciliationReport
          ? response.data.clearedTransactions
          : response.data.values;

      if (!records.length && type !== ReportTypes.BankReconciliationReport) throw Error("noData");

      exportHeaders = exportHeaders.map(({ getValue, ...header }) => ({
        ...header,
        value: getValue || header.value
      }));

      if (type) {
        let data;
        //custom csv 'header fields' for cash report and reconciliation reports
        if (type === ReportTypes.BankReconciliationReport) {
          data = [
            { rowName: "Beginning Balance:", rowValue: response.data.beginningBalance },
            { rowName: "Ending Balance:", rowValue: response.data.endingBalance },
            { rowName: "Report Dates:", rowValue: `${formatDate(response.data.dateFrom)} - ${formatDate(response.data.dateTo)}` }
          ]
        }
        else if (type === ReportTypes.OutstandingTransactionsReport) {
          let totalAmnt = records.reduce((total, trans) => {
            trans.TransAmount.split(',').map(t => total = total.minus(t));
            return total;
          }, new BigNumber(0));  
          totalAmnt = parseFloat(totalAmnt);
          let availBalance = new BigNumber(response.data.openingBalance || 0).plus(totalAmnt || 0).toFixed(2);
          data = [
            { rowName: "Report Date:", rowValue: new Date().toLocaleString() },
            { rowName: "Book Balance:", rowValue: response.data.openingBalance },
            { rowName: "Total Outstanding Transactions Amount:", rowValue: totalAmnt },
            { rowName: "Bank Balance:", rowValue: parseFloat(availBalance) }
          ]
        }
        else {
          data = [
            { rowName: "Report Dates:", rowValue: (filterSettings.startDate || filterSettings.endDate) && `${formatDate(filterSettings.startDate + " 00:00:0000")} - ${formatDate(filterSettings.endDate + " 00:00:0000")}` },
            { rowName: "Opening Balance:", rowValue: response.data.openingBalance }
          ]
        }
        
        const csvPre = convertToCSV([
          { label: this.props.fundName, value: "rowName", default: "" },
          { label: " ", value: "rowValue", default: "" },
        ], data);

        const csv = csvPre + "\r\n\r\n" + convertToCSV(exportHeaders, records);

        downloadCSV(csv, reportName + ".xls");
      }

      this.setState({ downloading: false }, this.close);
    } catch (error) {
      this.setState({ downloading: false, [error.message || "error"]: true });
    }
  }

  render() {
    const {
      error,
      fields,
      groupBy,
      downloading,
      noData,
      type,
      submitAttempted
    } = this.state;

    const {
      allowGroupedResults,
      reportType,
      show
    } = this.props;

    const reportFields = getReportFields(type);

    return (
      <Modal modalShowing={show}>
        <div className="modal-container">
          <div className="modal new-fund-modal xl-modal">
            <div className="bordered-modal-header">
              <h4 className="modal-page-header">
                New {reportType ? ReportTypes[reportType] : "Accounting Report"}
              </h4>
              <i
                className="icon ion-ios-close-empty"
                onClick={this.close.bind(this)}
              />
            </div>
            <div className="modal-section">
              {!reportType && (
                <RadioGroup
                  name="type"
                  onChange={(name, value) => this.onToggleReportType(value)}
                  options={Object.keys(ReportTypes)
                    .slice(0, 4)
                    .map(type => ({
                      key: ReportTypes[type],
                      value: ReportTypes[type]
                    }))}
                  title="Report Type"
                  value={type}
                />
              )}

              <div className="mb-16">
                <div className="flex">
                  <label
                    className={`input-label ${
                      submitAttempted && !fields.length ? "error-label" : ""
                      }`}
                  >
                    Report Fields
                  </label>
                  {submitAttempted && !fields.length && (
                    <span className="xs-text error-text ml-8">
                      * Please select report fields
                    </span>
                  )}
                </div>
                <div className="modal-row report-fields-grid">
                  {reportFields.map(field => (
                    <div className="custom-check-container" key={field.value}>
                      <input
                        type="checkbox"
                        id={`report-field-${field.value}`}
                        className="custom-check"
                        name={field.value}
                        onChange={this.onToggleReportFields}
                        checked={fields.indexOf(field.value) >= 0}
                        disabled={field.disabled || false}
                      />
                      <label
                        htmlFor={`report-field-${field.value}`}
                        className="secondary-text"
                      >
                        {field.label}
                      </label>
                    </div>
                  ))}
                </div>
              </div>
              
              {type == ReportTypes.CheckRegisterReport &&
                <div className="modal-row">
                  <input
                    className="custom-check"
                    type="checkbox"
                    id="excludeVoidedChecks"
                    name="excludeVoidedChecks"
                    onChange={this.excludeVoidedChecks} />
                  <label htmlFor="excludeVoidedChecks">Exclude Voided Checks</label>
                </div>
              }

              {allowGroupedResults && fields.indexOf("Balance") < 0 && (
                <div className="modal-row">
                  <div className="column-half column-half-left">
                    <label className="input-label">
                      Remove Duplicates Of
                    </label>
                    <Select
                      clearable={true}
                      options={reportFields
                        .filter(field => fields.indexOf(field.value) >= 0)
                        .map(field => ({
                          key: field.value,
                          value: field.label
                        }))}
                      value={groupBy}
                      name="groupBy"
                      onChange={e =>
                        this.onChange(e.target.name, e.target.value)
                      }
                    />
                  </div>
                </div>
              )}
            </div>
            <div className="modal-btns text-right">
              <button
                className="btn btn-accent"
                onClick={this.close.bind(this)}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={this.exportData.bind(this)}
                disabled={downloading}
              >
                {downloading ? "Downloading Report" : "Download Report"}
              </button>
              <FadeOutErrorMessage show={error} />
              <FadeOutErrorMessage
                show={noData}
                message="No data was found matching the specified dates."
                timeOut={5000}
              />
            </div>
          </div>
        </div>
      </Modal>
    );
  }
}



// WEBPACK FOOTER //
// ./src/components/accountingComponents/fundDetails/ReportModal.jsx